import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { dataModelerSelectCurrentUiState } from '../../../../redux/selectors/dataModeler';
import { defaultFieldTypeParams, emptyCustomColumnDraft } from '../utils';
import { useCallback, useMemo } from 'react';
import {
  dataModelerSetDerivedColumnDraft,
  IDerivedColumnDraft,
} from '../../../../redux/slices/dataModeler';
import {
  FieldType,
  IFieldTypeParamsFieldsFragment,
  IMappedStringDefinitionInput,
  IRawSqlDefinitionInput,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useAvailableColumnsForSelection } from '../DataModelerFieldDropdown/utils';
import { z } from 'zod';
import {
  getFieldColumnSchemaValidator,
  validationErrorToString,
} from '../validation';
import { assertNotNil } from '../../../../utils/types';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  viewKey: string;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
  addDerivedColumn: (
    column: string,
    type: FieldType,
    typeParams: IFieldTypeParamsFieldsFragment,
  ) => void;
}

const useNewDerivedColumnModel = ({
  viewKey,
  tableKey,
  availableColumnsFromServer,
  addDerivedColumn,
}: IProps) => {
  const dispatch = useAppDispatch();
  const stateDraftColumn = useAppSelector(dataModelerSelectCurrentUiState)
    ?.newDerivedColumns[tableKey];
  const draftColumn = stateDraftColumn ?? emptyCustomColumnDraft;

  const updateDraft = useCallback(
    (props: Partial<IDerivedColumnDraft> | null) => {
      if (props) {
        const draft = {
          ...draftColumn,
          ...props,
        };
        dispatch(dataModelerSetDerivedColumnDraft({ tableKey, draft }));
      } else {
        dispatch(dataModelerSetDerivedColumnDraft({ tableKey, draft: null }));
      }
    },
    [draftColumn, dispatch, tableKey],
  );

  const { columnName, fieldType, typeParams, definitionType } = draftColumn;

  const setType = useCallback(
    (fieldType: FieldType) => {
      const updateTypeParams =
        fieldType === FieldType.Array && !typeParams.arrayItemType
          ? defaultFieldTypeParams({
              arrayItemType: FieldType.String,
            })
          : typeParams;
      updateDraft({ fieldType, typeParams: updateTypeParams });
    },
    [typeParams, updateDraft],
  );

  const setTypeParams = useCallback(
    (newParams: IFieldTypeParamsFieldsFragment) => {
      updateDraft({ typeParams: newParams });
    },
    [updateDraft],
  );

  const { items } = useAvailableColumnsForSelection({
    viewKey,
    tableKey,
    availableColumnsFromServer,
  });

  const columnValidator = useMemo(() => {
    const otherColumnNames = new Set(items.map((item) => item.column));
    return z.object({
      value: getFieldColumnSchemaValidator(otherColumnNames),
    });
  }, [items]);

  const columnNameError = useMemo(() => {
    const parsed = columnValidator.safeParse({ value: columnName });
    if (parsed.error) {
      return validationErrorToString(parsed.error);
    }
    return null;
  }, [columnName, columnValidator]);

  const handleColumnNameChange = useCallback(
    (newValue: string) => {
      updateDraft({ columnName: newValue });
    },
    [updateDraft],
  );

  const handleConfirmRawSqlInput = useCallback(
    (rawSql: IRawSqlDefinitionInput) => {
      addDerivedColumn(columnName, fieldType, {
        ...typeParams,
        customDefinition: {
          __typename: 'RawSqlDefinitionMeta',
          column: columnName,
          rawSql: rawSql.rawSql,
          fullDataRequired: rawSql.fullDataRequired,
        },
      });
    },
    [addDerivedColumn, columnName, fieldType, typeParams],
  );

  const handleConfirmMappedStringInput = useCallback(
    (mappedString: IMappedStringDefinitionInput) => {
      const { mappedColumn, mapElse, mapNull, values } = mappedString;
      addDerivedColumn(columnName, fieldType, {
        ...typeParams,
        customDefinition: {
          __typename: 'MappedStringDefinitionMeta',
          column: columnName,
          mappedColumn,
          mapElse,
          mapNull,
          values: values.map((pairInput) => {
            return {
              __typename: 'MappedValuePair',
              ...pairInput,
            };
          }),
        },
      });
    },
    [addDerivedColumn, columnName, fieldType, typeParams],
  );

  const handleConfirm = useCallback(() => {
    assertNotNil(definitionType);
    switch (definitionType) {
      case 'RawSql':
        assertNotNil(draftColumn.definition?.rawSql);
        handleConfirmRawSqlInput(draftColumn.definition.rawSql);
        break;
      case 'MappedString':
        assertNotNil(draftColumn.definition?.mappedString);
        handleConfirmMappedStringInput(draftColumn.definition.mappedString);
        break;
      default:
        throw new UnreachableCaseError(definitionType);
    }
    updateDraft(null);
  }, [
    definitionType,
    draftColumn.definition?.mappedString,
    draftColumn.definition?.rawSql,
    handleConfirmMappedStringInput,
    handleConfirmRawSqlInput,
    updateDraft,
  ]);

  const handleCancel = useCallback(() => {
    updateDraft(null);
  }, [updateDraft]);

  return {
    columnNameError,
    handleColumnNameChange,
    handleCancel,
    handleConfirm,
    setType,
    setTypeParams,
    draftColumn,
    stateDraftColumn,
    updateDraft,
  };
};

export default useNewDerivedColumnModel;
